import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faDownload, faFilter, faRefresh, faSort, faSortDown, faSortUp, faTimes, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useHistoryStore } from './store/useHistoryStore';
import { useCurrentSalonStore } from '@stores/useCurrentSalonStore';
import { useToast } from '@hooks/useToast';
import { urlSyncUtils, useUrlSync } from '@hooks/useUrlSync';
import UniversalPaginationCallback from '@components/pagination/UniversalPaginationCallback';
import type { AppBookingListItemDto } from '@api/generated';

const HistoryPage: React.FC = () => {
  const { currentSalon } = useCurrentSalonStore();
  const { success: showToast } = useToast();
  const {
    bookings,
    summary,
    pagination,
    loading,
    error,
    filters,
    loadBookings,
    loadBookingsWithFilters,
    loadSummary,
    setFilters,
    resetFilters,
    quickExportBookings,
    clearError,
  } = useHistoryStore();

  // URL synchronizace pro stránkování
  const [urlState, setUrlState] = useUrlSync('history', {
    defaults: {
      page: 1, // 1-based pro URL (UniversalPaginationCallback používá 1-based)
      size: 10,
    },
    keyMapping: {
      page: 'page',
      size: 'size',
    },
    serialize: {
      page: urlSyncUtils.number.serialize,
      size: urlSyncUtils.number.serialize,
    },
    deserialize: {
      page: urlSyncUtils.number.deserialize,
      size: urlSyncUtils.number.deserialize,
    },
  });

  // Local UI state
  const [showFilters, setShowFilters] = useState(false);

  // Synchronizace URL stavu s filtry v store
  useEffect(() => {
    if (urlState.page !== undefined && urlState.page !== (filters.page || 0) + 1) {
      // URL má jinou stránku než store, aktualizuj store
      setFilters({ page: urlState.page - 1 }); // Převedeme 1-based na 0-based
    }
    if (urlState.size !== undefined && urlState.size !== filters.size) {
      // URL má jinou velikost stránky než store, aktualizuj store
      setFilters({ size: urlState.size, page: 0 }); // Reset na první stránku při změně velikosti
    }
  }, [urlState, filters.page, filters.size, setFilters]);

  // Load data on mount and when currentSalon changes
  useEffect(() => {
    if (currentSalon?.salonId) {
      const loadData = async () => {
        await Promise.all([loadBookingsWithFilters({ salonId: currentSalon.salonId }), loadSummary({ salonId: currentSalon.salonId })]);
      };
      loadData();
    }
  }, [currentSalon?.salonId, loadBookingsWithFilters, loadSummary]);

  // Load data when filters change
  useEffect(() => {
    if (currentSalon?.salonId) {
      loadBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentSalon?.salonId]);

  const handleFilterChange = (key: keyof typeof filters, value: string | number) => {
    setFilters({ [key]: value, page: 0 });
    // Reset na první stránku v URL při změně filtrů
    setUrlState({ page: 1 });
  };

  const handleSort = (sortBy: string) => {
    const currentSortBy = filters.sortBy;
    const currentSortOrder = filters.sortOrder;

    let newSortOrder: 'asc' | 'desc' = 'desc';
    if (currentSortBy === sortBy && currentSortOrder === 'desc') {
      newSortOrder = 'asc';
    }

    setFilters({ sortBy, sortOrder: newSortOrder, page: 0 });
    // Reset na první stránku v URL
    setUrlState({ page: 1 });
  };

  const handlePageChange = (page: number) => {
    // Aktualizuj URL
    setUrlState({ page });
    // Store se aktualizuje automaticky přes useEffect
  };

  const handleExport = async (format: string = 'CSV') => {
    try {
      const data = await quickExportBookings({}, format);

      // Create and download file
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rezervace_${new Date().toISOString().split('T')[0]}.${format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast('Export byl úspěšně dokončen', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Export se nezdařil', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
    }).format(amount);
  };

  const getStateColor = (state: string) => {
    switch (state?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStateLabel = (state: string) => {
    switch (state?.toLowerCase()) {
      case 'confirmed':
        return 'Potvrzeno';
      case 'pending':
        return 'Čeká na potvrzení';
      case 'cancelled':
        return 'Zrušeno';
      case 'completed':
        return 'Dokončeno';
      default:
        return state;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faTimes} className="text-red-400 mr-2" />
              <p className="text-red-800">{error}</p>
              <button onClick={clearError} className="ml-auto bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700">
                Zkusit znovu
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Historie rezervací</h1>
              <p className="text-gray-600 mt-1">Přehled všech rezervací v salonu {currentSalon?.salonName}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>Filtry</span>
              </button>
              <button onClick={() => handleExport('CSV')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                <FontAwesomeIcon icon={faDownload} />
                <span>Export</span>
              </button>
              <button
                onClick={() => {
                  if (currentSalon?.salonId) {
                    loadBookings();
                    loadSummary({ salonId: currentSalon.salonId });
                  }
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faRefresh} className={loading ? 'animate-spin' : ''} />
                <span>Obnovit</span>
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FontAwesomeIcon icon={faCalendar} className="text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem rezervací</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.totalBookings}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkové tržby</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalRevenue)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FontAwesomeIcon icon={faUser} className="text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Potvrzené</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.summaryByState?.confirmed?.count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FontAwesomeIcon icon={faTimes} className="text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Zrušené</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.summaryByState?.cancelled?.count || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow mb-6 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum od</label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={e => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum do</label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={e => handleFilterChange('dateTo', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vyhledávání</label>
                <input
                  type="text"
                  placeholder="Hledat v jménech a službách..."
                  value={filters.query || ''}
                  onChange={e => handleFilterChange('query', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stav</label>
                <select
                  value={filters.state || ''}
                  onChange={e => handleFilterChange('state', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Všechny stavy</option>
                  <option value="pending">Čeká na potvrzení</option>
                  <option value="confirmed">Potvrzeno</option>
                  <option value="completed">Dokončeno</option>
                  <option value="cancelled">Zrušeno</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  resetFilters();
                  setUrlState({ page: 1 });
                }}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                Resetovat filtry
              </button>
            </div>
          </div>
        )}

        {/* Bookings Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('code')} className="flex items-center space-x-1 hover:text-gray-700">
                      <span>Kód</span>
                      <FontAwesomeIcon icon={filters.sortBy === 'code' ? (filters.sortOrder === 'asc' ? faSortUp : faSortDown) : faSort} className="text-xs" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('customer')} className="flex items-center space-x-1 hover:text-gray-700">
                      <span>Zákazník</span>
                      <FontAwesomeIcon icon={filters.sortBy === 'customer' ? (filters.sortOrder === 'asc' ? faSortUp : faSortDown) : faSort} className="text-xs" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('worker')} className="flex items-center space-x-1 hover:text-gray-700">
                      <span>Zaměstnanec</span>
                      <FontAwesomeIcon icon={filters.sortBy === 'worker' ? (filters.sortOrder === 'asc' ? faSortUp : faSortDown) : faSort} className="text-xs" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('start')} className="flex items-center space-x-1 hover:text-gray-700">
                      <span>Datum a čas</span>
                      <FontAwesomeIcon icon={filters.sortBy === 'start' ? (filters.sortOrder === 'asc' ? faSortUp : faSortDown) : faSort} className="text-xs" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služby</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('totalAmount')} className="flex items-center space-x-1 hover:text-gray-700">
                      <span>Cena</span>
                      <FontAwesomeIcon icon={filters.sortBy === 'totalAmount' ? (filters.sortOrder === 'asc' ? faSortUp : faSortDown) : faSort} className="text-xs" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={() => handleSort('state')} className="flex items-center space-x-1 hover:text-gray-700">
                      <span>Stav</span>
                      <FontAwesomeIcon icon={filters.sortBy === 'state' ? (filters.sortOrder === 'asc' ? faSortUp : faSortDown) : faSort} className="text-xs" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <FontAwesomeIcon icon={faRefresh} className="animate-spin mr-2" />
                      Načítání rezervací...
                    </td>
                  </tr>
                ) : bookings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      Žádné rezervace nenalezeny
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking: AppBookingListItemDto) => (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{booking.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            {booking.customer?.firstName} {booking.customer?.lastName}
                          </div>
                          <div className="text-gray-500">{booking.customer?.phone}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{booking.worker?.displayName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{formatDate(booking.start)}</div>
                          <div className="text-gray-500">do {formatDate(booking.end)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="space-y-1">
                          {booking.services?.map((service, index) => (
                            <div key={index} className="text-sm">
                              {service.name}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(booking.totalAmount || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStateColor(booking.state || '')}`}>
                          {getStateLabel(booking.state || '')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Zobrazeno <span className="font-medium">{pagination.page * pagination.size + 1}</span>
                  {' - '}
                  <span className="font-medium">{Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)}</span>
                  {' z '}
                  <span className="font-medium">{pagination.totalElements}</span>
                  {' výsledků'}
                </div>
                <UniversalPaginationCallback
                  totalPages={pagination.totalPages}
                  currentPage={pagination.page + 1} // Převedeme 0-based na 1-based pro komponentu
                  loading={loading}
                  onPageChange={handlePageChange}
                  showPageNumbers={true}
                  maxVisiblePages={5}
                  className=""
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
